import { FormEvent, useContext, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import AnswerAlternative from './AnswerAlternative';
import IconButton from '../../../../../components/atoms/button/IconButton';
import Input from '../../../../../components/atoms/input';
import TextArea from '../../../../../components/atoms/text-area/TextArea';
import { AnswerAlternativeData, QuizContentData, QuizContentSpecificData } from '../../../../../types/contentTypes';
import AlertContext from '../../../../../utils/AlertContext';
import { ContentFormProps } from '../../ContentEditor';

import './QuizContentForm.scss';

const DEFAULT_ANSWER_ALTERNATIVES = [
  { key: uuidv4(), answer: '', correct: true, feedback: '' },
  { key: uuidv4(), answer: '', correct: false, feedback: '' },
];

export default function QuizContentForm({
  content,
  onNewContentSubmit,
  onExistingContentSubmit,
}: ContentFormProps<QuizContentData, QuizContentSpecificData>) {
  const [headline, setHeadline] = useState<string>(content?.headline || '');
  const [description, setDescription] = useState<string>(content?.description || '');
  const [answerAlternatives, setAnswerAlternatives] = useState<AnswerAlternativeData[]>(
    content?.answerAlternatives || DEFAULT_ANSWER_ALTERNATIVES
  );
  const [question, setQuestion] = useState<string>(content?.question || '');

  const showAlert = useContext(AlertContext);

  const showDelete = answerAlternatives.length > 2;
  // TODO: Come up with a way to deep compare answer alternatives, lodash's isEqual does not seem to reliably work (14/11/22)
  /*  const changes =
    !!content &&
    (headline !== content.headline ||
      isEqual(answerAlternatives, content.answerAlternatives) ||
      question !== content.question);*/

  function handleAnswerChange(value: string, index: number) {
    const newAnswerAlternatives = answerAlternatives.map((alternative, i) => {
      if (i === index) {
        return { ...alternative, answer: value };
      } else {
        return alternative;
      }
    });
    setAnswerAlternatives(newAnswerAlternatives);
  }

  function handleFeedbackChange(value: string, index: number) {
    const newAnswerAlternatives = answerAlternatives.map((alternative, i) => {
      if (i === index) {
        return { ...alternative, feedback: value };
      } else {
        return alternative;
      }
    });
    setAnswerAlternatives(newAnswerAlternatives);
  }

  function handleDelete(index: number) {
    const newAnswerAlternatives = answerAlternatives.filter((_, i) => i !== index);
    setAnswerAlternatives(newAnswerAlternatives);
  }

  function handleAdd() {
    const newAnswerAlternatives = [
      ...answerAlternatives,
      {
        key: uuidv4(),
        answer: '',
        correct: false,
        feedback: '',
      },
    ];
    setAnswerAlternatives(newAnswerAlternatives);
  }

  function handleMarkAsCorrect(index: number) {
    if (!content) return;
    const newAnswerAlternatives = answerAlternatives.map((alternative, i) =>
      i === index ? { ...alternative, correct: true } : { ...alternative, correct: false }
    );
    setAnswerAlternatives(newAnswerAlternatives);
  }

  const AnswerAlternatives = answerAlternatives.map((alternative, index) => (
    <li key={alternative.key} className="mb-6">
      <AnswerAlternative
        answerAlternative={alternative}
        index={index}
        onMarkAsCorrect={() => handleMarkAsCorrect(index)}
        onAnswerChange={value => handleAnswerChange(value, index)}
        onFeedbackChange={value => handleFeedbackChange(value, index)}
        onDelete={showDelete ? () => handleDelete(index) : undefined}
      />
    </li>
  ));

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!content) {
      onNewContentSubmit?.({
        headline,
        description,
        answerAlternatives,
        question,
      });
    } else {
      onExistingContentSubmit?.({
        ...content,
        description,
        headline,
        answerAlternatives,
        question,
      });
    }
    showAlert('Changes saved!');
  }

  return (
    <form className="quiz-content-form" onSubmit={handleSubmit}>
      <label className="block font-medium mb-2" htmlFor="headline">
        Question headline
      </label>
      <Input className="w-full mb-6" id="headline" state={[headline, setHeadline]} />
      <label className="block font-medium mb-2" htmlFor="description">
        Question description
      </label>
      <TextArea
        className="w-full mb-6"
        id="description"
        state={[description, setDescription]}
        placeholder="Enter a description of the question"
        rows={4}
      />
      <label className="block font-medium mb-2" htmlFor="question">
        Question
      </label>
      <Input className="mb-6 w-full" id="question" state={[question, setQuestion]} />
      <ol className="mb-8">{AnswerAlternatives}</ol>
      <IconButton className="mb-16" icon="add" label="Add answer alternative" onClick={handleAdd} />
      <IconButton icon="save-outline" label={!content ? 'Save and add to section' : 'Save changes'} type="submit" />
    </form>
  );
}
